import React, { useState } from 'react';
import Modal from '../../Modal';
import { RSButton } from 'reactsymbols-kit';
import Styles from '../../../styles/colors';
import ModelSettingsForm, { type ModelSettings } from '../../ModelSettingsForm';
import type { IModelInformation } from '../../../stores/projects/model_information';
import { rsValue } from '../../Inspector/rsForm';

/** Props for the inner {@link ChangeSettings} content. */
interface ChangeSettingsContentProps {
  /** The current model name shown in the form. */
  name: string;
  /** The model whose settings are edited. */
  model: IModelInformation;
  /** Close the modal. */
  onRequestClose?: () => void;
  /** Cancel and close the modal. */
  onCancel?: () => void;
}

/** Props for the {@link ChangeSettingsModal} wrapper (the default export). */
interface ChangeSettingsModalProps extends ChangeSettingsContentProps {
  /** Whether the modal is open. */
  isOpen: boolean;
}

/**
 * Change the settings of the model.
 */
function ChangeSettings({ name, model, onRequestClose, onCancel }: ChangeSettingsContentProps) {
  // The settings fields are edited together by ModelSettingsForm via partial
  // merge updates (onChangeProperty did this.setState(change)). useState
  // replaces rather than merges, so these live in a single object we spread-merge
  // into — preserving the class's setState merge semantics exactly. Initialised
  // from the model props (was the constructor).
  const [values, setValues] = useState<ModelSettings>({
    name,
    notation: model.notation,
    generateMethods: model.generateMethods,
    propagateOnDependency: model.propagateOnDependency,
    propagateOnMethod: model.propagateOnMethod,
  });
  const [saving, setSaving] = useState(false);

  // Handler for the save button
  const onSave = () => {
    setSaving(true);
    model
      .changeSettings(
        values.name,
        values.notation,
        values.generateMethods,
        values.propagateOnDependency,
        values.propagateOnMethod
      )
      .then(() => {
        setSaving(false);
        if (onRequestClose) onRequestClose();
      });
  };

  // onSubmit handler to allow to press enter while the form is in focus
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!saving) {
      onSave();
    }
  };

  // onChange handler for the ModelSettingsForm (partial merge update)
  const onChangeProperty = (change: Partial<ModelSettings>) => {
    setValues((current) => ({ ...current, ...change }));
  };

  return (
    <div className="merlin-modal change-settings-modal">
      <div className="title">Change model settings</div>
      <form className="form" onSubmit={onSubmit}>
        <div className="modal-body">
          <div className="container">
            <ModelSettingsForm
              onChange={onChangeProperty}
              values={{
                name: values.name,
                notation: values.notation,
                generateMethods: values.generateMethods,
                propagateOnMethod: values.propagateOnMethod,
                propagateOnDependency: values.propagateOnDependency,
              }}
              disabled={saving}
            />
          </div>
        </div>
        <div className="button-wrapper">
          <RSButton
            value="Cancel"
            type="button"
            background={Styles.defaultGradient}
            color={Styles.darkTextColor}
            onClick={onCancel}
          />
          <RSButton
            type="submit"
            background={Styles.blueGradient}
            value={rsValue(saving ? null : 'Save')}
            iconName={rsValue(saving ? 'FaSpinner' : null)}
            iconSize={15}
            className={rsValue(saving ? 'spin' : null)}
          />
        </div>
      </form>
    </div>
  );
}

// Wrap the ChangeSettings component in a Modal
const ChangeSettingsModal = ({ isOpen, onRequestClose, ...rest }: ChangeSettingsModalProps) => (
  <Modal isOpen={isOpen} onRequestClose={onRequestClose}>
    <ChangeSettings onRequestClose={onRequestClose} {...rest} />
  </Modal>
);

export default ChangeSettingsModal;
